import React, { Fragment, useCallback } from "react";
import { AnimatePresence } from "framer-motion";
import classNames from "classnames";
import ItineraryMetadataContainer from "./ItineraryMetadataContainer";
import ItineraryStopsContainer from "./ItineraryStopsContainer";
import LoaderWithFading from "../loader/LoaderWithFading";
import Fade from "../animations/Fade";
import { StickyContainer } from "react-sticky";
import ItineraryDayZero from "./ItineraryDayZero";
import ItineraryPrimaryHeader from "./ItineraryPrimaryHeader";
import { useTranslate } from "../../translate";
import ItinerarySwitcherPopup from "./ItinerarySwitcherPopup";
import { useMountedState } from "react-use";
import { createPortal } from "react-dom";
import { useElementQueries } from "./utils";
import PropTypes from "prop-types";
import { useModal } from "../../hooks";
import ItineraryEmailForm from "./ItineraryEmailForm";
import { useFixHTML } from "./hooks";
import ItineraryEndTween from "./ItineraryEndTween";

const containerSizes = [450, 500, 545, 650, 700];

function Itinerary(props) {
    const {
        data,
        itineraryId,
        activeStopId,
        itineraryMetadataClassName,
        itineraryItemsClassName,
        renderMap,
        formattedStops,
        setLoopDirection,
        loopDirection,
        loaderClassName,
        dayTopOffset,
        activeDayId,
        itineraryItemsPaddingBottom,
        enableChangeItinerary,
        onItineraryIdChange,
        setStartingPoint,
        startingPoint,
        routeItineraries,
        setIsOpenItinerarySwitcher,
        isOpenItinerarySwitcher,
        inLauncherModal,
        inPlanner,
        useTwoColumnLayout,
        useOldLayout,
        cardsOffsetTop,
        showToast,
        activeSuggestionId,
    } = props;
    const translate = useTranslate();
    const ItemsContainer = useTwoColumnLayout ? Fragment : StickyContainer;
    const itineraryMetadata = data?.itineraryData?.metadata;
    const stopsRaw = data?.itineraryData?.stops;
    const isMounted = useMountedState();
    const closeItinerarySwticherPopup = useCallback(() => setIsOpenItinerarySwitcher(false), [
        setIsOpenItinerarySwitcher,
    ]);
    const showItinerarySwitcher = enableChangeItinerary;
    const [metadataRef, { attrs: metadataAttrs }] = useElementQueries(containerSizes);
    const [, setIsEmailModalVisible, EmailModal, emailModalProps] = useModal();
    const arrivalDay = useFixHTML(itineraryMetadata?.arrivalDay);

    return (
        <Fragment>
            {createPortal(
                <EmailModal {...emailModalProps}>
                    <ItineraryEmailForm></ItineraryEmailForm>
                </EmailModal>,
                document.querySelector("#portal"),
            )}
            <AnimatePresence exitBeforeEnter initial={true}>
                {!data && <LoaderWithFading key="loader" className={loaderClassName} />}
                {data && (
                    <>
                        <Fade
                            key="itineraryMetadata"
                            className={itineraryMetadataClassName}
                            ref={metadataRef}
                            {...metadataAttrs}
                        >
                            {showItinerarySwitcher &&
                                isMounted &&
                                createPortal(
                                    <ItinerarySwitcherPopup
                                        isOpenItinerarySwitcher={isOpenItinerarySwitcher}
                                        onItineraryIdChange={onItineraryIdChange}
                                        itinerary={data.itineraryData}
                                        routeItineraries={routeItineraries}
                                        onClose={closeItinerarySwticherPopup}
                                    />,
                                    document.querySelector(".js-primary-layout-popups"),
                                )}
                            {(inLauncherModal || showItinerarySwitcher) && (
                                <ItineraryPrimaryHeader
                                    showCloseBtn={inLauncherModal}
                                    setIsOpenItinerarySwitcher={setIsOpenItinerarySwitcher}
                                    showItinerarySwitcher={showItinerarySwitcher}
                                    hideShadow={!!activeDayId}
                                />
                            )}
                            <ItineraryMetadataContainer
                                data={data}
                                itineraryId={itineraryId}
                                formattedStops={formattedStops}
                                setLoopDirection={setLoopDirection}
                                loopDirection={loopDirection}
                                setStartingPoint={setStartingPoint}
                                className="js__itinerary-metadata"
                                withTopSpacing={!!(inLauncherModal || showItinerarySwitcher)}
                            />
                            <ItineraryEndTween
                                title="Start of itinerary"
                                description={arrivalDay}
                                className="trpr-itinerary-tween"
                            ></ItineraryEndTween>
                            {false && <ItineraryDayZero itineraryMetadata={itineraryMetadata} />}
                        </Fade>
                        <Fade
                            key="itineraryItems"
                            className={classNames(itineraryItemsClassName, "js__itinerary-items")}
                            style={{ paddingBottom: itineraryItemsPaddingBottom }}
                            {...metadataAttrs}
                        >
                            <ItemsContainer>
                                {renderMap()}
                                <ItineraryStopsContainer
                                    formattedStops={formattedStops}
                                    itineraryId={itineraryId}
                                    stopsRaw={stopsRaw}
                                    loopDirection={loopDirection}
                                    activeStopId={activeStopId}
                                    itineraryMetadata={data.itineraryData.metadata}
                                    dayTopOffset={dayTopOffset}
                                    activeDayId={activeDayId}
                                    startingPoint={startingPoint}
                                    routeType={data?.routeData?.routeType}
                                    setIsOpenItinerarySwitcher={setIsOpenItinerarySwitcher}
                                    showItinerarySwitcher={showItinerarySwitcher}
                                    inLauncherModal={inLauncherModal}
                                    inPlanner={inPlanner}
                                    useTwoColumnLayout={useTwoColumnLayout}
                                    useOldLayout={useOldLayout}
                                    cardsOffsetTop={cardsOffsetTop}
                                    activeSuggestionId={activeSuggestionId}
                                    setIsEmailModalVisible={setIsEmailModalVisible}
                                    showToast={showToast}
                                />
                            </ItemsContainer>
                        </Fade>
                    </>
                )}
            </AnimatePresence>
        </Fragment>
    );
}

Itinerary.propTypes = {
    useTwoColumnLayout: PropTypes.bool.isRequired,
};

export default Itinerary;
